import { useCallback } from 'react';

import { TimeQuestionType } from '@/features/timeGame/types';

import type { Question } from '@/features/gameSession/types';

type SelectedTime = {
	hours: number;
	minutes: number;
};

interface UseTimeGameQuestionParams {
	currentQuestion?: Question;
	onAnswerSubmitted: (isCorrect: boolean, isSecondAttempt: boolean) => void;
}

interface UseTimeGameQuestionReturn {
	submitAnswer: (selectedTime: SelectedTime, isAnalogAnswer?: boolean, isSecondAttempt?: boolean) => void;
	checkAnswer: (selectedTime: SelectedTime, isAnalogAnswer?: boolean, isAnalogQuestion?: boolean) => boolean;
}

export const parseTimeString = (value?: string): SelectedTime | null => {
	if (!value) return null;
	const match = value.match(/(\d{1,2})\s*[:.]\s*(\d{2})/);
	if (!match) return null;

	const hours = Number.parseInt(match[1], 10);
	const minutes = Number.parseInt(match[2], 10);
	if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
		return null;
	}

	return { hours, minutes };
};

export const isSelectedTimeCorrect = (
	selected: SelectedTime,
	expected?: string,
	isAnalogAnswer?: boolean,
	isAnalogQuestion?: boolean,
): boolean => {
	const parsedExpected = parseTimeString(expected);
	if (!parsedExpected) return false;

	// Use modulo 12 comparison when either:
	// 1. User is answering with analog clock (can't specify AM/PM)
	// 2. Question shows analog clock (doesn't indicate AM/PM, so both 00:35 and 12:35 are correct)
	if (isAnalogAnswer || isAnalogQuestion) {
		return parsedExpected.minutes === selected.minutes && parsedExpected.hours % 12 === selected.hours % 12;
	}

	return parsedExpected.hours === selected.hours && parsedExpected.minutes === selected.minutes;
};

/**
 * Custom hook for handling time game question logic
 */
const useTimeGameQuestion = ({
	currentQuestion,
	onAnswerSubmitted,
}: UseTimeGameQuestionParams): UseTimeGameQuestionReturn => {
	// Check if answer is correct without submitting
	const checkAnswer = useCallback(
		(selectedTime: SelectedTime, isAnalogAnswer?: boolean, isAnalogQuestion?: boolean) => {
			if (!currentQuestion) {
				return false;
			}

			const answers = currentQuestion.answers ?? [];
			const correctAnswer = answers.find((answer) => answer?.is_correct) ?? answers[0];
			const parsedExpected = parseTimeString(correctAnswer?.answer_display);
			const selectedHours24 =
				isAnalogAnswer && parsedExpected
					? parsedExpected.hours // map analog selection to expected day part for submission
					: selectedTime.hours;
			const selectedForValidation: SelectedTime = {
				hours: selectedHours24,
				minutes: selectedTime.minutes,
			};

			return isSelectedTimeCorrect(
				selectedForValidation,
				correctAnswer?.answer_display,
				isAnalogAnswer,
				isAnalogQuestion,
			);
		},
		[currentQuestion],
	);

	const submitAnswer = useCallback(
		(selectedTime: SelectedTime, isAnalogAnswer?: boolean, isSecondAttempt = false) => {
			if (!currentQuestion) {
				return;
			}

			const answers = currentQuestion.answers ?? [];
			const correctAnswer = answers.find((answer) => answer?.is_correct) ?? answers[0];
			const parsedExpected = parseTimeString(correctAnswer?.answer_display);
			const selectedHours24 =
				isAnalogAnswer && parsedExpected
					? parsedExpected.hours // map analog selection to expected day part for submission
					: selectedTime.hours;
			const selectedForValidation: SelectedTime = {
				hours: selectedHours24,
				minutes: selectedTime.minutes,
			};

			// Check if question shows analog clock (e.g., "AnalogToDigital")
			const questionType = currentQuestion.type || '';
			const isAnalogQuestion = questionType.includes(TimeQuestionType.AnalogToDigital);

			const isCorrect = isSelectedTimeCorrect(
				selectedForValidation,
				correctAnswer?.answer_display,
				isAnalogAnswer,
				isAnalogQuestion,
			);

			onAnswerSubmitted(isCorrect, isSecondAttempt);
		},
		[currentQuestion, onAnswerSubmitted],
	);

	return {
		submitAnswer,
		checkAnswer,
	};
};

export default useTimeGameQuestion;
